function decodeEntities(value) {
  return value.replace(/&(?:amp|lt|gt|quot|apos|#(?:\d+|x[\da-f]+));/gi, entity => {
    const named = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&apos;': "'" };
    const key = entity.toLowerCase();
    if (named[key]) return named[key];
    const point = key.startsWith('&#x') ? parseInt(key.slice(3, -1), 16) : Number(key.slice(2, -1));
    return point > 0 && point <= 0x10ffff ? String.fromCodePoint(point) : entity;
  });
}

function escapeMarkup(value) {
  return value.replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' })[character]);
}

function safeMessageUrl(value) {
  if (typeof value !== 'string') return null;
  const target = value.trim();
  if (!/^https?:\/\//i.test(target) || /\s|[\u0000-\u001f\u007f]/.test(target)) return null;
  try {
    const url = new URL(target);
    return ['http:', 'https:'].includes(url.protocol) && url.hostname && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

function anchorParts(source) {
  const anchor = /^<a\b([^>]*)>([\s\S]*?)<\/a\s*>$/i.exec(source);
  if (!anchor) return { label: source, target: '' };
  const href = /(?:^|\s)href\s*=\s*(["'])([\s\S]*?)\1/i.exec(anchor[1]);
  return { target: href ? decodeEntities(href[2]) : '', label: decodeEntities(anchor[2].replace(/<[^>]*>/g, '')) };
}

function serializedAnchor(target, label) {
  return '<a href="' + escapeMarkup(target) + '">' + escapeMarkup(label.replace(/\s+/g, ' ').trim()) + '</a>';
}

function xmlNodeText(node) {
  if (node.nodeType === 3 || node.nodeType === 4) return node.textContent;
  if (node.nodeType !== 1) return '';
  if (node.localName === 'a') return serializedAnchor(node.getAttribute('href') || '', node.textContent);
  return Array.from(node.childNodes, xmlNodeText).join('');
}

function messageText(content) {
  if (typeof content !== 'string') return JSON.stringify(content);
  if (!/^\s*<response(?:\s|>)/i.test(content)) return content;
  if (typeof DOMParser !== 'undefined') {
    const parsed = new DOMParser().parseFromString(content, 'application/xml');
    if (parsed.querySelector('parsererror')) return content;
    const message = parsed.querySelector('response > message');
    return message ? Array.from(message.childNodes, xmlNodeText).join('').trim() : content;
  }
  // Ohne DOMParser bleibt derselbe begrenzte XML-Vertrag für die Node-Tests verfügbar.
  const message = /<message(?:\s[^>]*)?>([\s\S]*?)<\/message>/i.exec(content);
  if (!message) return content;
  const body = message[1].replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, (_, text) => escapeMarkup(text)).replace(/<!--[\s\S]*?-->/g, '');
  const plain = text => decodeEntities(text.replace(/<[^>]*>/g, ''));
  let result = '';
  let start = 0;
  for (const match of body.matchAll(/<a\b[^>]*>[\s\S]*?<\/a\s*>/gi)) {
    result += plain(body.slice(start, match.index));
    const link = anchorParts(match[0]);
    result += serializedAnchor(link.target, link.label);
    start = match.index + match[0].length;
  }
  return (result + plain(body.slice(start))).trim();
}

function bareUrl(source) {
  let target = source;
  let previous;
  do {
    previous = target;
    target = target.replace(/[.,;:!?]+$/, '');
    for (const [closing, opening] of [[')', '('], [']', '['], ['}', '{']]) {
      while (target.endsWith(closing) && target.split(closing).length > target.split(opening).length) target = target.slice(0, -1);
    }
  } while (target !== previous);
  return { target, suffix: source.slice(target.length) };
}

function renderMessage(row, content) {
  const make = (tag, text, parent) => {
    const node = document.createElement(tag);
    if (text !== undefined) node.textContent = text;
    parent.appendChild(node);
    return node;
  };
  const link = (parent, label, target, fallback) => {
    const href = safeMessageUrl(target);
    if (!href) return make('span', fallback ?? label, parent);
    const shortName = new URL(href).host.replace(/^www\./i, '');
    const name = label.trim();
    const anchor = make('a', !name || safeMessageUrl(name) === href ? shortName : name, parent);
    anchor.className = 'sana-message-link';
    anchor.setAttribute('href', href);
    anchor.setAttribute('title', href);
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
    return anchor;
  };
  const inline = (parent, text, emphasis = true) => {
    const tokens = /<a\b[^>]*>[\s\S]*?<\/a\s*>|\[([^\]\r\n]+)\]\(((?:[^\s()<>]|\([^()\s<>]*\))+)\)|https?:\/\/[^\s<>"']+|\*\*([^*]+)\*\*|\*([^*]+)\*/gi;
    let start = 0;
    for (const match of text.matchAll(tokens)) {
      if (match.index > start) make('span', text.slice(start, match.index), parent);
      if (/^<a\b/i.test(match[0])) {
        const anchor = anchorParts(match[0]);
        link(parent, anchor.label, anchor.target);
      } else if (match[1] !== undefined) {
        link(parent, match[1], match[2], match[0]);
      } else if (/^https?:\/\//i.test(match[0])) {
        const url = bareUrl(match[0]);
        link(parent, '', url.target, url.target);
        if (url.suffix) make('span', url.suffix, parent);
      } else if (emphasis) {
        inline(make('strong', undefined, parent), match[3] || match[4], false);
      } else make('span', match[0], parent);
      start = match.index + match[0].length;
    }
    if (start < text.length) make('span', text.slice(start), parent);
  };
  const text = messageText(content).replace(/<a\b[^>]*>[\s\S]*?<\/a\s*>/gi, source => {
    const anchor = anchorParts(source);
    return serializedAnchor(anchor.target, anchor.label);
  });
  let list = null;
  for (const line of text.split(/\r?\n/)) {
    if (!line.trim()) { list = null; continue; }
    const bullet = /^\s*(?:[•*-])\s+(.+)$/.exec(line);
    if (bullet) {
      if (!list) list = make('ul', undefined, row);
      inline(make('li', undefined, list), bullet[1]);
    } else {
      list = null;
      inline(make('p', undefined, row), line);
    }
  }
}


function validEndpoint(value) {
  if (typeof value !== 'string' || !/^https?:\/\/[^\s/?#]+(?:[/?#]|$)/i.test(value)) return false;
  try {
    const parsed = new URL(value);
    return (parsed.protocol === 'https:' || parsed.protocol === 'http:') && Boolean(parsed.hostname);
  } catch { return typeof URL === 'undefined'; }
}

function newSessionId() {
  return 'uid_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2);
}

function responseMessage(payload) {
  if (!payload || typeof payload.response !== 'string') throw new Error('Missing response');
  const xml = payload.response.trim();
  if (!/^<response(?:\s[^>]*)?>[\s\S]*<\/response>$/i.test(xml)) throw new Error('Invalid response XML');
  if (typeof DOMParser !== 'undefined') {
    const parsed = new DOMParser().parseFromString(xml, 'application/xml');
    if (parsed.querySelector('parsererror')) throw new Error('Invalid response XML');
    const message = parsed.querySelector('response > message');
    if (!message) throw new Error('Missing message');
    const value = (node, selector) => node.querySelector(selector)?.textContent.trim() || '';
    return {
      message: messageText(xml),
      actions: Array.from(parsed.querySelectorAll('response > ui > actions > action'), action => ({
        type: action.getAttribute('type') || 'link', label: value(action, 'label'),
        url: value(action, 'url'), payload: value(action, 'payload')
      })).slice(0, 5),
      chips: Array.from(parsed.querySelectorAll('response > ui > chips > chip'), node => node.textContent.trim()).filter(Boolean).slice(0, 5),
      carousels: Array.from(parsed.querySelectorAll('response > ui > carousels > carousel'), carousel =>
        Array.from(carousel.querySelectorAll('items > item'), item => ({
          title: value(item, 'title'), subtitle: value(item, 'subtitle'), image: value(item, 'image'),
          price: value(item, 'price'), url: value(item, ':scope > url'), ctaUrl: value(item, 'cta > url'),
          ctaLabel: value(item, 'cta > label'), ctaPayload: value(item, 'cta > payload')
        })).slice(0, 10))
    };
  }
  // The Node DOM test environment has no DOMParser. Read only the supported XML fields.
  const elements = (source, tag) => Array.from(source.matchAll(new RegExp(`<${tag}(?:\\s[^>]*)?>([\\s\\S]*?)<\\/${tag}>`, 'gi')), match => match[1]);
  const decode = value => value.replace(/<[^>]*>/g, '').replace(/&(?:amp|lt|gt|quot|apos|#(?:\d+|x[\da-f]+));/gi, entity => {
    const named = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&apos;': "'" };
    const key = entity.toLowerCase();
    if (named[key]) return named[key];
    const point = key.startsWith('&#x') ? parseInt(key.slice(3, -1), 16) : Number(key.slice(2, -1));
    return point > 0 && point <= 0x10ffff ? String.fromCodePoint(point) : entity;
  }).trim();
  const value = (source, tag) => decode(elements(source, tag)[0] || '');
  const message = elements(xml, 'message')[0];
  if (message === undefined) throw new Error('Missing message');
  return {
    message: messageText(xml),
    actions: Array.from((elements(xml, 'actions')[0] || '').matchAll(/<action\b([^>]*)>([\s\S]*?)<\/action>/gi), match => ({
      type: /\btype\s*=\s*["']([^"']+)["']/i.exec(match[1])?.[1] || 'link',
      label: value(match[2], 'label'), url: value(match[2], 'url'), payload: value(match[2], 'payload')
    })).slice(0, 5),
    chips: elements(elements(xml, 'chips')[0] || '', 'chip').map(decode).filter(Boolean).slice(0, 5),
    carousels: elements(xml, 'carousel').map(carousel => elements(carousel, 'item').slice(0, 10).map(item => ({
      title: value(item, 'title'), subtitle: value(item, 'subtitle'), image: value(item, 'image'),
      price: value(item, 'price'), url: value(item.replace(/<cta(?:\s[^>]*)?>[\s\S]*?<\/cta>/gi, ''), 'url'),
      ctaUrl: value(elements(item, 'cta')[0] || '', 'url'), ctaLabel: value(item, 'label'), ctaPayload: value(item, 'payload')
    })))
  };
}

function safeContentUrl(value) {
  if (typeof value !== 'string' || !/^https?:\/\//i.test(value) || /[\s\u0000-\u001f\u007f]/.test(value)) return null;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) && url.hostname && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}


function productPayload(item) {
  if (typeof item.ctaPayload === 'string') {
    try { return JSON.parse(item.ctaPayload); } catch {}
  }
  return item.ctaPayload;
}

function productUrl(item) {
  const payload = productPayload(item);
  // Produkt-Payloads enthalten häufig ein JSON-Objekt statt einer reinen URL.
  const payloadUrl = payload && typeof payload === 'object' ? payload.url : payload;
  for (const candidate of [item.url, item.ctaUrl, payloadUrl]) {
    const url = safeContentUrl(candidate);
    if (url) return url;
  }
  return null;
}

const translations = {
  de: ['Hallo! Ich bin hier, um zu helfen. 👋', 'Chat öffnen', '← Kontakt', 'Zurück zur Kontaktwahl', 'Verlauf löschen', 'Chat leeren', 'Chat schließen', 'Hallo! Ich bin Sophie 👋', 'Wie möchtest Du uns am liebsten kontaktieren?', 'Web Chat', 'Direktes Gespräch', 'Nachricht', 'Schreiben Sie Ihre Nachricht...', 'Senden', 'Chatverlauf löschen', 'Gesprächsverlauf wirklich löschen?', 'Abbrechen', 'Löschen', 'Hallo! Ich bin dein PURMEO Support.🌿 Wobei darf ich helfen?', 'Produktinformationen', 'Bestellung verfolgen', 'Lieferadresse ändern', 'Ungültige Endpunkt-Konfiguration. Bitte den Betreiber kontaktieren.', 'Warte auf Antwort', 'Fehler bei der Anfrage. Bitte erneut versuchen.', 'Ich möchte mehr über {product} erfahren.', 'Antwort kopieren', 'Kopiert', 'Text markiert — über die Tastatur kopieren'],
  en: ['Hello! I am here to help. 👋', 'Open chat', '← Contact', 'Back to contact options', 'Delete history', 'Clear chat', 'Close chat', 'Hello! How can I help? 👋', 'How would you like to contact us?', 'Web Chat', 'Direct conversation', 'Message', 'Type your message...', 'Send', 'Delete chat history', 'Delete this conversation?', 'Cancel', 'Delete', 'Hello! How can we help?', 'Product information', 'Track an order', 'Change delivery address', 'Invalid endpoint configuration. Please contact the site owner.', 'Waiting for a reply', 'Request failed. Please try again.', 'I would like to learn more about {product}.', 'Copy answer', 'Copied', 'Text selected — copy with your keyboard'],
  fr: ['Bonjour ! Je suis là pour vous aider. 👋', 'Ouvrir le chat', '← Contact', 'Retour aux contacts', "Supprimer l’historique", 'Effacer le chat', 'Fermer le chat', 'Bonjour ! Comment vous aider ? 👋', 'Comment souhaitez-vous nous contacter ?', 'Chat en ligne', 'Conversation directe', 'Message', 'Écrivez votre message...', 'Envoyer', "Supprimer l’historique du chat", 'Supprimer cette conversation ?', 'Annuler', 'Supprimer', 'Bonjour ! Comment pouvons-nous vous aider ?', 'Informations produit', 'Suivre une commande', 'Modifier l’adresse de livraison', 'Configuration du serveur incorrecte. Contactez le site.', 'En attente de réponse', 'Échec de la demande. Veuillez réessayer.', 'Je souhaite en savoir plus sur {product}.', 'Copier la réponse', 'Copié', 'Texte sélectionné — copiez avec le clavier'],
  it: ['Ciao! Sono qui per aiutarti. 👋', 'Apri chat', '← Contatti', 'Torna ai contatti', 'Elimina cronologia', 'Svuota chat', 'Chiudi chat', 'Ciao! Come posso aiutarti? 👋', 'Come preferisci contattarci?', 'Chat online', 'Conversazione diretta', 'Messaggio', 'Scrivi il tuo messaggio...', 'Invia', 'Elimina cronologia chat', 'Eliminare questa conversazione?', 'Annulla', 'Elimina', 'Ciao! Come possiamo aiutarti?', 'Informazioni sul prodotto', 'Traccia un ordine', 'Modifica indirizzo di consegna', 'Configurazione del server non valida. Contatta il sito.', 'In attesa di risposta', 'Richiesta non riuscita. Riprova.', 'Vorrei saperne di più su {product}.', 'Copia risposta', 'Copiato', 'Testo selezionato — copia con la tastiera'],
  es: ['¡Hola! Estoy aquí para ayudarte. 👋', 'Abrir chat', '← Contacto', 'Volver a contactos', 'Eliminar historial', 'Vaciar chat', 'Cerrar chat', '¡Hola! ¿Cómo puedo ayudarte? 👋', '¿Cómo prefieres contactarnos?', 'Chat en línea', 'Conversación directa', 'Mensaje', 'Escribe tu mensaje...', 'Enviar', 'Eliminar historial del chat', '¿Eliminar esta conversación?', 'Cancelar', 'Eliminar', '¡Hola! ¿Cómo podemos ayudarte?', 'Información del producto', 'Seguir un pedido', 'Cambiar dirección de entrega', 'Configuración del servidor no válida. Contacta con el sitio.', 'Esperando respuesta', 'La solicitud ha fallado. Inténtalo de nuevo.', 'Me gustaría saber más sobre {product}.', 'Copiar respuesta', 'Copiado', 'Texto seleccionado — copia con el teclado']
};

function languageTag(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  try { return Intl.getCanonicalLocales(value.trim().replace(/_/g, '-'))[0] || null; }
  catch { return null; }
}

function supportedLanguage(value) {
  const language = String(value || '').toLowerCase().replace('_', '-').split('-')[0];
  return Object.hasOwn(translations, language) ? language : null;
}

function mountWidget(root, config = {}) {
  config = { ...config };
  const valueOf = value => typeof value === 'function' ? value() : value;
  const languageOf = () => languageTag(valueOf(config.language)) || languageTag(document.documentElement?.lang)
    || languageTag(config.defaultLanguage) || 'de';
  let language = languageOf();
  const labelLanguage = () => supportedLanguage(language) || supportedLanguage(config.defaultLanguage) || 'en';
  const branding = config.branding || {};
  const localized = (value, fallback) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const defaultLanguage = languageTag(config.defaultLanguage) || 'de';
      for (const candidate of [language, language.split('-')[0], defaultLanguage, defaultLanguage.split('-')[0]]) {
        const key = Object.keys(value).find(key => key.toLowerCase().replace(/_/g, '-') === candidate.toLowerCase());
        if (key !== undefined && value[key] != null) return value[key];
      }
      return fallback;
    }
    return value ?? fallback;
  };
  const text = index => translations[labelLanguage()][index];
  const bindings = [];
  const translate = (element, index, attribute = null) => {
    bindings.push({ element, index, attribute });
  };
  let destroyed = false;
  let generation = 0;
  let controller = null;
  const timers = [];
  const later = (callback, delay) => {
    if (typeof setTimeout === 'function') timers.push(setTimeout(() => { if (!destroyed) callback(); }, delay));
  };
  const storagePrefix = 'sanaexpert:' + JSON.stringify([config.endpoint || '', config.botId || 'default', config.environment || 'live', config.storageKey || '']);
  const sessionKey = storagePrefix + ':session';
  const historyKey = storagePrefix + ':history';
  const make = (tag, marker, label, parent = root) => {
    const element = document.createElement(tag);
    if (marker) element.setAttribute('data-widget', marker);
    if (tag === 'button') element.setAttribute('type', 'button');
    if (label) element.textContent = label;
    parent.appendChild(element);
    return element;
  };
  const named = (tag, id, label, parent) => {
    const element = make(tag, null, label, parent);
    element.id = id;
    element.setAttribute('id', id);
    return element;
  };
  const image = (parent, src, alt) => {
    if (!src) return null;
    const element = make('img', null, '', parent);
    element.setAttribute('src', src);
    element.setAttribute('alt', alt);
    return element;
  };
  const customBrand = Boolean(branding.name || branding.logoUrl);
  const purmeoLogo = customBrand ? safeContentUrl(branding.logoUrl) : 'https://purmeo.de/cdn/shop/files/Purmeo_LOGO_grun_DE.png';
  const shell = named('div', 'sanaexpert-chat-widget');
  shell.setAttribute('data-sana-widget', '');
  shell.setAttribute('data-custom-brand', String(customBrand));
  if (config.inline) shell.setAttribute('data-inline', 'true');
  const bubble = named('div', 'sanaexpert-floating-message', '', shell);
  bubble.className = 'floating-message';
  const bubbleContent = make('div', null, '', bubble);
  bubbleContent.className = 'message-content';
  const bubbleText = make('span', null, 'Hallo! Ich bin hier, um zu helfen. 👋', bubbleContent);
  bubbleText.className = 'message-text';
  const bubbleAvatar = make('div', null, '', bubble);
  bubbleAvatar.className = 'message-avatar';
  image(bubbleAvatar, purmeoLogo, branding.name || 'SanaExpert');
  const open = named('button', 'sanaexpert-chat-button', '', shell);
  open.className = 'chat-button';
  open.setAttribute('data-widget', 'open');
  open.setAttribute('aria-label', 'Chat öffnen');
  const buttonContent = make('div', null, '', open);
  buttonContent.className = 'button-content';
  image(buttonContent, 'https://assets.streamlinehq.com/image/private/w_240,h_240,ar_1/f_auto/v1/icons/chat-bubble/chat-bubble-oval-notification-9sspoyy6jdke0q6z55u01t.png/chat-bubble-oval-notification-lyp5qd8jdlaojh8ha996a.png', 'Chat');
  const badge = make('div', null, '1', buttonContent);
  badge.className = 'notification-badge';
  const panel = named('div', 'sanaexpert-chat-container', '', shell);
  panel.className = 'chat-container';
  panel.setAttribute('data-widget', 'panel');
  const header = make('div', null, '', panel);
  header.className = 'chat-header';
  const headerBrand = make('div', null, '', header);
  headerBrand.className = 'chat-brand';
  image(headerBrand, customBrand ? purmeoLogo : 'https://purmeo.de/cdn/shop/t/9/assets/purmeo_logo.png', branding.name || 'Purmeo Logo');
  if (branding.name) make('strong', null, branding.name, headerBrand);
  const headerActions = make('div', null, '', header);
  headerActions.className = 'chat-header-actions';
  const back = make('button', 'back', '← Kontakt', headerActions);
  back.setAttribute('aria-label', 'Zurück zur Kontaktwahl');
  back.setAttribute('title', 'Zurück zur Kontaktwahl');
  back.hidden = true;
  const reset = named('button', 'reset-session', '🧹', headerActions);
  reset.setAttribute('aria-label', 'Verlauf löschen');
  reset.setAttribute('title', 'Chat leeren');
  const close = make('button', 'close', '×', headerActions);
  close.setAttribute('aria-label', 'Chat schließen');
  const contact = named('div', 'contact-options-container', '', panel);
  contact.className = 'contact-options-container';
  const welcome = make('div', null, '', contact);
  welcome.className = 'welcome-section';
  const welcomeAvatar = make('div', null, '', welcome);
  welcomeAvatar.className = 'welcome-avatar';
  image(welcomeAvatar, customBrand ? purmeoLogo : 'https://cdn-icons-png.flaticon.com/512/4128/4128253.png', branding.name || 'PURMEO');
  const welcomeTitle = make('h2', null, 'Hallo! Ich bin Sophie 👋', welcome);
  welcomeTitle.className = 'welcome-title';
  const welcomeSubtitle = make('p', null, 'Wie möchtest Du uns am liebsten kontaktieren?', welcome);
  welcomeSubtitle.className = 'welcome-subtitle';
  const webChat = named('button', 'web-chat-button', '', contact);
  webChat.className = 'contact-option webchat-option';
  const optionIcon = make('span', null, '', webChat);
  optionIcon.className = 'option-icon webchat-icon';
  optionIcon.setAttribute('aria-hidden', 'true');
  make('span', null, '', optionIcon).className = 'webchat-symbol';
  const optionContent = make('span', null, '', webChat);
  optionContent.className = 'option-content';
  const optionTitle = make('span', null, 'Web Chat', optionContent);
  optionTitle.className = 'option-title';
  const optionSubtitle = make('span', null, 'Direktes Gespräch', optionContent);
  optionSubtitle.className = 'option-subtitle';
  const optionArrow = make('span', null, '→', webChat);
  optionArrow.className = 'option-arrow';
  optionArrow.setAttribute('aria-hidden', 'true');
  const messages = make('div', 'messages', '', panel);
  messages.className = 'chat-transcript';
  const addAssistantAvatar = () => {
    const avatar = make('div', null, '', messages);
    avatar.className = 'assistant-avatar';
    image(avatar, purmeoLogo, branding.name || 'María');
  };
  addAssistantAvatar();
  const inputArea = make('div', null, '', panel);
  inputArea.className = 'input-area';
  const input = make('input', 'input', '', inputArea);
  input.setAttribute('aria-label', 'Nachricht');
  input.setAttribute('placeholder', 'Schreiben Sie Ihre Nachricht...');
  const send = make('button', 'send', 'Senden', inputArea);
  const status = make('div', 'status', '', panel);
  status.setAttribute('role', 'status');
  const resetDialog = make('div', null, '', panel);
  resetDialog.className = 'reset-dialog';
  resetDialog.setAttribute('role', 'dialog');
  resetDialog.setAttribute('aria-label', 'Chatverlauf löschen');
  resetDialog.hidden = true;
  const resetCard = make('div', null, '', resetDialog);
  resetCard.className = 'reset-dialog-card';
  const resetQuestion = make('p', null, '', resetCard);
  const resetActions = make('div', null, '', resetCard);
  resetActions.className = 'reset-dialog-actions';
  const cancelReset = make('button', null, 'Abbrechen', resetActions);
  cancelReset.className = 'reset-dialog-cancel';
  const approveReset = make('button', null, 'Löschen', resetActions);
  approveReset.className = 'reset-dialog-approve';
  for (const [element, index, attribute] of [
    [bubbleText, 0], [open, 1, 'aria-label'], [back, 2], [back, 3, 'aria-label'], [back, 3, 'title'],
    [reset, 4, 'aria-label'], [reset, 5, 'title'], [close, 6, 'aria-label'], [welcomeTitle, 7],
    [welcomeSubtitle, 8], [optionTitle, 9], [optionSubtitle, 10], [input, 11, 'aria-label'],
    [input, 12, 'placeholder'], [send, 13], [resetDialog, 14, 'aria-label'], [resetQuestion, 15],
    [cancelReset, 16], [approveReset, 17]
  ]) translate(element, index, attribute);
  const refreshLanguage = () => {
    language = languageOf();
    shell.setAttribute('lang', language);
    for (const { element, index, attribute } of bindings) {
      if (attribute) element.setAttribute(attribute, text(index));
      else element.textContent = text(index);
    }
    if (customBrand) welcomeTitle.textContent = localized(branding.greeting, labelLanguage() === 'de' ? 'Hallo! Wie können wir helfen? 👋' : text(7));
  };
  refreshLanguage();

  let store = null;
  try { store = localStorage; } catch {}
  let sessionId = null;
  let history = [];
  try {
    sessionId = store && store.getItem(sessionKey);
    const saved = store && store.getItem(historyKey);
    if (saved) history = JSON.parse(saved);
    if (!Array.isArray(history)) history = [];
  } catch { history = []; }
  if (!sessionId) sessionId = newSessionId();
  try { if (store) store.setItem(sessionKey, sessionId); } catch {}
  const persist = () => {
    try { if (store) store.setItem(historyKey, JSON.stringify(history)); } catch {}
  };
  const renderAnswer = (row, content, reply) => {
    const answer = make('div', null, '', row);
    answer.className = 'chat-answer';
    renderMessage(answer, content);
    renderReply(row, reply);
    const copy = make('button', 'copy-answer', text(26), row);
    copy.className = 'copy-answer';
    copy.setAttribute('aria-live', 'polite');
    copy.addEventListener('click', async () => {
      if (destroyed) return;
      try {
        await navigator.clipboard.writeText(answer.innerText ?? answer.textContent);
        if (!destroyed) copy.textContent = text(27);
      } catch {
        // Bei gesperrter Zwischenablage nur den Antworttext zur manuellen Kopie markieren.
        const selection = window.getSelection?.();
        if (selection && document.createRange) {
          const range = document.createRange();
          range.selectNodeContents(answer);
          selection.removeAllRanges();
          selection.addRange(range);
          copy.textContent = text(28);
        }
      }
    });
  };
  const addMessage = (value, type) => {
    const content = type === 'bot' ? value.message : value;
    const row = make(type === 'bot' ? 'div' : 'p', null, type === 'bot' ? '' : content, messages);
    row.className = 'chat-turn ' + type;
    if (type === 'bot') {
      renderAnswer(row, content, value);
    }
    history.push(type === 'bot' ? { type, content, ui: value } : { type, content });
    persist();
  };
  const renderReply = (row, reply) => {
    if (!reply || typeof reply !== 'object') return;
    for (const items of reply.carousels || []) {
      const carousel = make('div', null, '', row);
      carousel.className = 'carousel';
      for (const item of items) {
        const card = make('div', null, '', carousel);
        card.className = 'carousel-card';
        const link = productUrl(item);
        const payload = productPayload(item);
        const productName = payload && typeof payload.product === 'string' ? payload.product.trim() : '';
        const question = productName ? text(25).replace('{product}', productName) : '';
        const productControl = (parent, label = '') => {
          const control = make(link ? 'a' : 'button', null, label, parent);
          if (link) {
            control.setAttribute('href', link);
            control.setAttribute('target', '_blank');
            control.setAttribute('rel', 'noopener noreferrer');
          } else {
            control.setAttribute('type', 'button');
            control.addEventListener('click', () => {
              if (busy || destroyed) return;
              input.value = question;
              submit();
            });
          }
          return control;
        };
        const imageUrl = safeContentUrl(item.image);
        if (imageUrl) {
          let imageParent = card;
          if (link || question) {
            imageParent = productControl(card);
            imageParent.className = 'carousel-card-image';
            imageParent.setAttribute('aria-label', item.ctaLabel || item.title || productName);
          }
          const picture = image(imageParent, imageUrl, item.title || '');
          picture.setAttribute('loading', 'lazy');
        }
        const content = make('div', null, '', card);
        content.className = 'carousel-card-content';
        for (const field of ['title', 'subtitle', 'price']) {
          if (!item[field]) continue;
          const label = make('div', null, String(item[field]), content);
          label.className = 'carousel-card-' + field;
        }
        if (item.ctaLabel) {
          const cta = make('div', null, '', content);
          cta.className = 'carousel-card-cta';
          if (link || question) {
            productControl(cta, item.ctaLabel);
          } else {
            make('span', null, item.ctaLabel, cta);
          }
        }
      }
    }
    let actions = null;
    for (const action of (reply.actions || []).slice(0, 5)) {
      if (!action.label) continue;
      const link = productUrl({ url: action.url, ctaPayload: action.payload });
      const postback = action.type === 'postback' && typeof action.payload === 'string' && action.payload.trim();
      if ((action.type !== 'postback' && !link) || (action.type === 'postback' && !postback)) continue;
      if (!actions) {
        actions = make('div', null, '', row);
        actions.className = 'actions-container';
      }
      const control = make(postback ? 'button' : 'a', null, action.label, actions);
      if (postback) {
        control.addEventListener('click', () => {
          if (busy || destroyed) return;
          input.value = postback;
          submit();
        });
      } else {
        control.setAttribute('href', link);
        control.setAttribute('target', '_blank');
        control.setAttribute('rel', 'noopener noreferrer');
      }
    }
    if (reply.chips?.length) {
      const chips = make('div', null, '', row);
      chips.className = 'chips-container';
      for (const label of reply.chips) {
        const chip = make('button', null, label, chips);
        chip.setAttribute('type', 'button');
        chip.addEventListener('click', () => {
          input.value = label;
          submit();
        });
      }
    }
  };
  const renderHistory = () => {
    messages.textContent = '';
    addAssistantAvatar();
    for (const turn of history) {
      if (turn && typeof turn.content === 'string') {
        const row = make(turn.type === 'bot' ? 'div' : 'p', null,
          turn.type === 'bot' ? '' : turn.content, messages);
        row.className = 'chat-turn ' + (turn.type === 'user' ? 'user' : 'bot');
        if (turn.type === 'bot') {
          renderAnswer(row, turn.content, turn.ui);
        }
      }
    }
  };
  renderHistory();
  const showInitialQuestions = () => {
    const greeting = make('div', null, '', messages);
    greeting.className = 'chat-turn bot';
    make('p', null, localized(branding.greeting, customBrand && labelLanguage() === 'de' ? 'Hallo! Wie können wir helfen?' : text(18)), greeting);
    const chips = make('div', null, '', greeting);
    chips.className = 'chips-container';
    for (const question of localized(branding.initialQuestions, [text(19), text(20), text(21)])) {
      const chip = make('button', null, question, chips);
      chip.addEventListener('click', () => {
        input.value = question;
        submit();
      });
    }
  };
  const showPanel = visible => {
    panel.hidden = !visible;
    open.hidden = visible || Boolean(config.inline);
    if (visible) bubble.hidden = true;
  };
  showPanel(false);
  contact.hidden = true;
  messages.hidden = true;
  inputArea.hidden = true;
  bubble.hidden = true;
  let chatStarted = history.length > 0;
  let showingContacts = !chatStarted;
  const openChat = () => {
    badge.hidden = true;
    showPanel(true);
    contact.hidden = !showingContacts;
    messages.hidden = showingContacts;
    inputArea.hidden = showingContacts;
    back.hidden = showingContacts;
  };
  open.addEventListener('click', openChat);
  webChat.addEventListener('click', () => {
    if (!chatStarted && history.length === 0) showInitialQuestions();
    chatStarted = true;
    showingContacts = false;
    contact.hidden = true;
    messages.hidden = false;
    inputArea.hidden = false;
    back.hidden = false;
    if (input.focus) input.focus();
  });
  back.addEventListener('click', () => {
    showingContacts = true;
    contact.hidden = false;
    messages.hidden = true;
    inputArea.hidden = true;
    back.hidden = true;
  });
  close.addEventListener('click', () => showPanel(false));
  if (!config.inline) {
    later(() => {
      if (!chatStarted && history.length === 0 && panel.hidden) {
        bubble.hidden = false;
        later(() => { bubble.hidden = true; }, 4000);
      }
    }, 2000);
  }
  bubble.addEventListener('click', openChat);
  reset.addEventListener('click', () => {
    resetDialog.hidden = false;
  });
  cancelReset.addEventListener('click', () => { resetDialog.hidden = true; });
  approveReset.addEventListener('click', () => {
    generation++;
    controller?.abort();
    busy = false;
    resetDialog.hidden = true;
    history = [];
    chatStarted = true;
    renderHistory();
    showInitialQuestions();
    status.textContent = '';
    try {
      if (store) {
        store.removeItem(historyKey);
        store.removeItem(sessionKey);
      }
    } catch {}
    sessionId = newSessionId();
    try { if (store) store.setItem(sessionKey, sessionId); } catch {}
  });

  let busy = false;
  const submit = async () => {
    if (busy || destroyed) return;
    refreshLanguage();
    const message = String(input.value || '').trim();
    if (!message) return;
    if (typeof config.request !== 'function' && !validEndpoint(config.endpoint)) {
      status.textContent = text(22);
      return;
    }
    busy = true;
    const requestGeneration = ++generation;
    controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const payload = { user_id: sessionId, message,
      cemail: config.cemail ?? '', cname: config.cname ?? ' ', language,
      page_url: valueOf(config.pageUrl) ?? (typeof location !== 'undefined' ? location.href : '') };
    addMessage(message, 'user');
    input.value = '';
    status.textContent = '';
    const typing = make('div', null, '', messages);
    typing.className = 'typing-animation';
    typing.setAttribute('role', 'status');
    const pendingAnnouncement = make('p', null, text(23), typing);
    pendingAnnouncement.className = 'typing-status';
    for (let index = 0; index < 3; index++) make('span', null, '', typing).setAttribute('aria-hidden', 'true');
    messages.scrollTop = messages.scrollHeight;
    let replyReceived = false;
    try {
      let responsePayload;
      if (typeof config.request === 'function') {
        responsePayload = await config.request(payload, { signal: controller?.signal });
      } else {
        const response = await fetch(config.endpoint, {
          method: 'POST',
          signal: controller?.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('HTTP request failed');
        responsePayload = await response.json();
      }
      if (destroyed || requestGeneration !== generation) return;
      const reply = responseMessage(responsePayload);
      addMessage(reply, 'bot');
      replyReceived = true;
      status.textContent = '';
    } catch {
      if (destroyed || requestGeneration !== generation) return;
      status.textContent = text(24);
    } finally {
      if (destroyed || requestGeneration !== generation) return;
      renderHistory();
      if (replyReceived) {
        const lastQuestion = Array.from(messages.children).reverse()
          .find(turn => turn.className === 'chat-turn user');
        if (lastQuestion?.scrollIntoView) lastQuestion.scrollIntoView({ block: 'start' });
      } else {
        messages.scrollTop = messages.scrollHeight;
      }
      busy = false;
    }
  };
  send.addEventListener('click', submit);
  input.addEventListener('keydown', event => {
    if (event.key === 'Enter') { event.preventDefault(); submit(); }
  });
  if (config.inline) {
    chatStarted = true;
    showingContacts = false;
    openChat();
    back.hidden = true;
    close.hidden = true;
    if (!history.length) showInitialQuestions();
  }
  return { open, close, input, send, reset,
    setDraft(value) {
      if (destroyed || busy || typeof value !== 'string') return false;
      // Zeilenumbrüche werden für das einzeilige Eingabefeld durch Leerzeichen ersetzt.
      input.value = value.replace(/\r\n|\r|\n/g, ' ');
      showingContacts = false;
      openChat();
      if (config.inline) back.hidden = true;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      if (input.focus) input.focus();
      return true;
    },
    updateContext(context = {}) {
      if (destroyed) return;
      for (const key of ['language', 'pageUrl']) {
        if (Object.hasOwn(context, key)) config[key] = context[key];
      }
      refreshLanguage();
      if (chatStarted && !history.length) { renderHistory(); showInitialQuestions(); }
    },
    destroy() {
      destroyed = true;
      generation++;
      controller?.abort();
      if (typeof clearTimeout === 'function') timers.forEach(clearTimeout);
      shell.remove();
    }
  };
}

const style = document.createElement('style');
style.textContent = "#sanaexpert-chat-widget{font-family:-apple-system,BlinkMacSystemFont,\"Segoe UI\",sans-serif;font-size:15px;font-weight:400;line-height:1.6;letter-spacing:normal;text-transform:none;color:var(--text-color);color-scheme:light;--primary-color:#426b31;--primary-dark:#315425;--chat-green:var(--primary-color);--chat-dark:var(--primary-dark);--secondary-color:#edf4e8;--text-color:#263323;--border-color:#d5dfcf;--chat-background:#f5f7f2;--chat-surface:#fff;--chat-muted:#53634c;--chat-link:#315a27;--chat-user:#e2eed9}\n#sanaexpert-chat-widget *,#sanaexpert-chat-widget *::before,#sanaexpert-chat-widget *::after{box-sizing:border-box}\n/* Formular- und Überschriftenregeln des einbettenden Themes nicht übernehmen. */\n#sanaexpert-chat-widget :is(button,input,h2,p,strong){font-family:inherit;font-style:normal;letter-spacing:normal;text-transform:none}\n#sanaexpert-chat-widget button{appearance:none;display:inline-flex;align-items:center;justify-content:center;gap:8px;height:auto;min-height:0;min-width:0;max-width:none;margin:0;padding:0;border:0;background:none;color:inherit;font-size:inherit;font-weight:500;line-height:1.4;white-space:normal;box-shadow:none;cursor:pointer}\n#sanaexpert-chat-widget button::before,#sanaexpert-chat-widget button::after{content:none}\n#sanaexpert-chat-widget button:disabled{cursor:default;opacity:.6}\n#sanaexpert-chat-widget :is(button,input,a):focus-visible{outline:2px solid var(--chat-link);outline-offset:3px}\n#sanaexpert-chat-widget #sanaexpert-floating-message{position:fixed;right:20px;bottom:100px;max-width:min(280px,calc(100vw - 48px));padding:16px;color:#fff;background:linear-gradient(135deg,var(--chat-green),var(--chat-dark));border-radius:20px 20px 20px 4px;box-shadow:0 20px 40px #0002;z-index:9997}\n#sanaexpert-chat-widget .message-content{color:#fff}\n#sanaexpert-chat-widget .message-text{font-size:14px;line-height:1.4}\n#sanaexpert-chat-widget .message-avatar{position:absolute;right:-8px;bottom:-8px;width:40px;height:40px;display:flex;align-items:center;justify-content:center;background:#fff;border-radius:50%;box-shadow:0 2px 8px #0002}\n#sanaexpert-chat-widget .message-avatar img{display:block;width:28px;height:auto;max-height:28px;object-fit:contain}\n#sanaexpert-chat-widget #sanaexpert-chat-button{position:fixed;right:20px;bottom:20px;width:64px;height:64px;padding:0;border:0;border-radius:50%;color:#fff;background:linear-gradient(135deg,var(--chat-green),var(--chat-dark));box-shadow:0 20px 40px #0002;cursor:pointer;z-index:9998}\n#sanaexpert-chat-widget .button-content{position:relative;width:100%;height:100%;display:flex;align-items:center;justify-content:center}\n#sanaexpert-chat-widget .button-content img{display:block;width:32px;height:auto;filter:brightness(0) invert(1)}\n#sanaexpert-chat-widget .notification-badge{position:absolute;top:8px;right:8px;width:20px;height:20px;display:flex;align-items:center;justify-content:center;background:#ff4444;color:#fff;border-radius:50%;font-size:11px;font-weight:600;line-height:1}\n#sanaexpert-chat-widget #sanaexpert-chat-container{position:fixed;right:100px;bottom:20px;width:min(400px,calc(100vw - 24px));height:min(720px,calc(100vh - 40px));height:min(720px,calc(100dvh - 40px));display:flex;flex-direction:column;color:var(--text-color);background:var(--chat-background);border-radius:20px;box-shadow:0 20px 40px #0003;overflow:hidden;z-index:9999;isolation:isolate}\n#sanaexpert-chat-widget #sanaexpert-chat-container .chat-header{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-shrink:0;min-height:72px;padding:16px;color:#fff;background:linear-gradient(135deg,var(--chat-green),var(--chat-dark))}\n#sanaexpert-chat-widget .chat-brand{display:flex;align-items:center;gap:10px;flex:1;min-width:0}\n#sanaexpert-chat-widget #sanaexpert-chat-container .chat-header img{display:block;width:108px;max-width:108px;height:36px;max-height:36px;flex-shrink:0;padding:5px 8px;background:#fff;border-radius:8px;object-fit:contain}\n#sanaexpert-chat-widget #sanaexpert-chat-container .chat-header strong{min-width:0;font-size:13px;font-weight:600;line-height:1.4;max-height:2.8em;overflow:hidden;overflow-wrap:anywhere;white-space:normal}\n#sanaexpert-chat-widget .chat-header-actions{display:flex;align-items:center;gap:6px;flex-shrink:0}\n#sanaexpert-chat-widget #sanaexpert-chat-container .chat-header button{display:flex;align-items:center;justify-content:center;flex-shrink:0;width:32px;height:32px;padding:0;border:0;border-radius:8px;background:#ffffff26;color:#fff;font-size:20px;font-weight:500;line-height:1}\n#sanaexpert-chat-widget #sanaexpert-chat-container .chat-header button:hover{background:#ffffff40}\n#sanaexpert-chat-widget #sanaexpert-chat-container .chat-header [data-widget=\"back\"]{width:auto;padding:0 8px;font-size:12px;white-space:nowrap}\n#sanaexpert-chat-widget #sanaexpert-chat-container #reset-session{padding:8px;border:1px solid var(--border-color);border-radius:10px;background:var(--secondary-color);color:var(--chat-link);font-size:16px}\n#sanaexpert-chat-widget #contact-options-container{display:flex;flex:1;min-height:0;flex-direction:column;align-items:center;justify-content:center;justify-content:safe center;gap:24px;padding:32px 24px;overflow-y:auto;text-align:center;background:var(--chat-background)}\n#sanaexpert-chat-widget .welcome-section{width:100%}\n#sanaexpert-chat-widget .welcome-avatar{width:80px;height:80px;margin:0 auto 20px;display:flex;align-items:center;justify-content:center;background:#fff;border-radius:50%;box-shadow:0 10px 25px #0001}\n#sanaexpert-chat-widget .welcome-avatar img{display:block;width:50px;height:auto;max-height:56px;object-fit:contain}\n#sanaexpert-chat-widget[data-custom-brand=\"true\"] .welcome-avatar{width:160px;border-radius:20px}\n#sanaexpert-chat-widget[data-custom-brand=\"true\"] .welcome-avatar img{width:128px}\n#sanaexpert-chat-widget .welcome-title{margin:0 0 8px;color:var(--text-color);font-size:22px;font-weight:700;line-height:1.3}\n#sanaexpert-chat-widget .welcome-subtitle{margin:0;color:var(--chat-muted);font-size:15px;font-weight:400;line-height:1.6}\n#sanaexpert-chat-widget .contact-option{display:flex;align-items:center;gap:14px;padding:18px 20px;background:var(--chat-surface);border:2px solid transparent;border-radius:16px;color:var(--text-color);box-shadow:0 4px 12px #0001}\n#sanaexpert-chat-widget #web-chat-button{display:flex;align-items:center;justify-content:flex-start;gap:14px;flex-shrink:0;width:100%;height:auto;min-height:88px;padding:16px;border:2px solid var(--border-color);border-radius:16px;background:var(--chat-surface);text-align:left;font-size:16px;line-height:1.4;white-space:normal}\n#sanaexpert-chat-widget #web-chat-button:hover{border-color:var(--chat-green);box-shadow:0 8px 20px #0001}\n#sanaexpert-chat-widget .option-icon{display:flex;align-items:center;justify-content:center;flex:0 0 48px;width:48px;height:48px;border-radius:12px;background:linear-gradient(135deg,var(--chat-green),var(--chat-dark))}\n#sanaexpert-chat-widget .webchat-symbol{position:relative;display:block;width:24px;height:18px;border:2px solid #fff;border-radius:7px}\n#sanaexpert-chat-widget .webchat-symbol::after{content:\"\";position:absolute;bottom:-6px;left:4px;width:7px;height:7px;background:var(--chat-green);border-left:2px solid #fff;border-bottom:2px solid #fff;transform:skewY(-30deg)}\n#sanaexpert-chat-widget .option-content{display:flex;flex:1;min-width:0;flex-direction:column;align-items:flex-start}\n#sanaexpert-chat-widget .contact-option .option-title{display:block;margin-bottom:8px;color:var(--text-color);font-size:16px;font-weight:600;line-height:1.4}\n#sanaexpert-chat-widget .contact-option .option-subtitle{display:block;color:var(--chat-muted);font-size:14px;font-weight:400;line-height:1.4}\n#sanaexpert-chat-widget .option-arrow{flex-shrink:0;color:var(--chat-link);font-size:22px;line-height:1}\n#sanaexpert-chat-widget #sanaexpert-chat-container .chat-transcript{display:flex;flex:1;min-height:0;flex-direction:column;gap:12px;overflow-y:auto;padding:16px;background:var(--chat-background);scrollbar-width:thin;overscroll-behavior:contain}\n#sanaexpert-chat-widget #sanaexpert-chat-container .chat-turn{flex-shrink:0;min-width:0;margin:0;padding:14px 16px;border:1px solid var(--border-color);border-radius:16px;background:var(--chat-surface);color:var(--text-color);font-size:15px;font-weight:400;line-height:1.6;white-space:pre-wrap;overflow-wrap:anywhere}\n#sanaexpert-chat-widget #sanaexpert-chat-container .chat-turn.user{align-self:flex-end;max-width:90%;background:var(--chat-user);border-color:transparent;border-bottom-right-radius:4px}\n#sanaexpert-chat-widget #sanaexpert-chat-container .chat-turn.bot p{margin:0 0 8px;font-size:inherit;line-height:inherit;color:inherit}\n#sanaexpert-chat-widget #sanaexpert-chat-container .chat-turn.bot p:last-child{margin-bottom:0}\n#sanaexpert-chat-widget #sanaexpert-chat-container .sana-message-link{color:var(--chat-link);text-decoration:underline;text-underline-offset:2px;overflow-wrap:anywhere}\n#sanaexpert-chat-widget #sanaexpert-chat-container .chat-turn.bot ul{margin:0 0 8px;padding-left:20px}\n#sanaexpert-chat-widget #sanaexpert-chat-container .reset-dialog{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding:24px;background:var(--chat-background);text-align:center;z-index:1}\n#sanaexpert-chat-widget .reset-dialog-card{width:100%;max-width:320px;padding:24px;border:1px solid var(--border-color);border-radius:16px;background:var(--chat-surface);color:var(--text-color);box-shadow:0 4px 12px #0002}\n#sanaexpert-chat-widget .reset-dialog-card p{margin:0 0 20px;font-size:15px;line-height:1.6}\n#sanaexpert-chat-widget .reset-dialog-actions{display:flex;gap:8px}\n#sanaexpert-chat-widget .reset-dialog-actions button{flex:1;min-height:44px;padding:10px 14px;font-size:14px;font-weight:600;line-height:1.4}\n#sanaexpert-chat-widget .reset-dialog-cancel{border:1px solid var(--border-color);border-radius:10px;background:var(--secondary-color);color:var(--chat-link)}\n#sanaexpert-chat-widget .reset-dialog-approve{border:0;border-radius:10px;background:var(--chat-dark);color:#fff}\n#sanaexpert-chat-widget .chips-container{display:flex;flex-wrap:wrap;gap:8px;margin-top:12px;white-space:normal}\n#sanaexpert-chat-widget .chips-container button{height:auto;min-height:36px;padding:8px 12px;border:1px solid var(--border-color);border-radius:18px;background:var(--secondary-color);color:var(--chat-link);font-size:13px;font-weight:500;line-height:1.4;text-align:left}\n#sanaexpert-chat-widget .chips-container button:hover{border-color:var(--chat-green)}\n#sanaexpert-chat-widget .carousel{display:flex;gap:12px;overflow-x:auto;padding:10px 2px;scroll-snap-type:x mandatory;white-space:normal}\n#sanaexpert-chat-widget .carousel-card{flex:0 0 220px;min-width:220px;max-width:260px;overflow:hidden;border:1px solid var(--border-color);border-radius:12px;background:var(--chat-surface);scroll-snap-align:start}\n#sanaexpert-chat-widget .carousel-card-image{display:block;width:100%;padding:0;border-radius:0}\n#sanaexpert-chat-widget .carousel-card img{display:block;width:100%;height:180px;padding:12px;object-fit:contain;background:#fff}\n#sanaexpert-chat-widget .carousel-card-content{display:flex;flex-direction:column;gap:6px;padding:12px}\n#sanaexpert-chat-widget .carousel-card-title{font-size:15px;font-weight:600;line-height:1.4}\n#sanaexpert-chat-widget .carousel-card-subtitle{font-size:13px;color:var(--chat-muted)}\n#sanaexpert-chat-widget .carousel-card-price{font-weight:600;color:var(--chat-link)}\n#sanaexpert-chat-widget .carousel-card-cta a,#sanaexpert-chat-widget .carousel-card-cta button,#sanaexpert-chat-widget .carousel-card-cta span{display:block;width:100%;margin-top:6px;padding:10px 12px;border-radius:8px;background:var(--chat-green);color:#fff;text-align:center;line-height:1.4}\n#sanaexpert-chat-widget .typing-animation{display:flex;flex-shrink:0;min-height:24px;align-items:center;gap:4px;margin:8px 0;color:var(--chat-link)}\n#sanaexpert-chat-widget .typing-animation .typing-status{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}\n#sanaexpert-chat-widget .typing-animation span{display:inline-block;flex-shrink:0;width:8px;height:8px;border-radius:50%;background:var(--chat-link);animation:sanaexpertTyping 1.4s infinite ease-in-out}\n#sanaexpert-chat-widget .typing-animation span:nth-of-type(2){animation-delay:.2s}\n#sanaexpert-chat-widget .typing-animation span:nth-of-type(3){animation-delay:.4s}\n@keyframes sanaexpertTyping{0%,60%,100%{transform:translateY(0);opacity:.4}30%{transform:translateY(-6px);opacity:1}}\n#sanaexpert-chat-widget .assistant-avatar{width:72px;height:32px;display:flex;align-items:center;justify-content:center;flex-shrink:0;background:#fff;border-radius:8px}\n#sanaexpert-chat-widget .assistant-avatar img{display:block;width:56px;height:auto;max-height:24px;object-fit:contain}\n#sanaexpert-chat-widget #sanaexpert-chat-container .input-area{display:flex;align-items:center;gap:8px;flex-shrink:0;padding:12px;border-top:1px solid var(--border-color);background:var(--chat-surface)}\n#sanaexpert-chat-widget #sanaexpert-chat-container .input-area input{appearance:none;flex:1;min-width:0;min-height:44px;height:auto;margin:0;padding:10px 12px;border:1px solid var(--border-color);border-radius:10px;background:var(--chat-background);color:var(--text-color);font-size:15px;font-weight:400;line-height:1.5}\n#sanaexpert-chat-widget #sanaexpert-chat-container .input-area input::placeholder{color:var(--chat-muted);opacity:1}\n#sanaexpert-chat-widget #sanaexpert-chat-container .input-area button{flex-shrink:0;min-height:44px;height:auto;padding:10px 16px;border:0;border-radius:10px;background:var(--chat-dark);color:#fff;font-size:14px;font-weight:600;line-height:1.4}\n#sanaexpert-chat-widget #sanaexpert-chat-container [data-widget=\"status\"]{flex-shrink:0;padding:8px 12px;color:#b91c1c;font-size:13px;line-height:1.4;overflow-wrap:anywhere;background:var(--chat-surface)}\n#sanaexpert-chat-widget #sanaexpert-chat-container [data-widget=\"status\"]:empty{display:none}\n/* Die bisherige PURMEO-Palette auch bei dunklem Systemdesign vollständig verwenden. */\n@media(prefers-color-scheme:dark){#sanaexpert-chat-widget{color-scheme:dark;--secondary-color:#303f2d;--text-color:#f0f5ec;--border-color:#50614b;--chat-background:#161e18;--chat-surface:#222e24;--chat-muted:#bdcab6;--chat-link:#b9dc9b;--chat-user:#34482e}#sanaexpert-chat-widget #sanaexpert-chat-container [data-widget=\"status\"]{color:#fca5a5}}\n@media(max-width:600px){#sanaexpert-chat-widget #sanaexpert-chat-container{right:12px;bottom:12px;width:calc(100vw - 24px);height:min(720px,calc(100vh - 24px));height:min(720px,calc(100dvh - 24px));border-radius:16px}#sanaexpert-chat-widget #sanaexpert-chat-button{right:16px;bottom:calc(20px + var(--mobile-sticky-bar-height,0px) + env(safe-area-inset-bottom,0px));width:56px;height:56px}#sanaexpert-chat-widget #sanaexpert-floating-message{right:16px;bottom:calc(96px + var(--mobile-sticky-bar-height,0px) + env(safe-area-inset-bottom,0px))}#sanaexpert-chat-widget #sanaexpert-chat-container .chat-header{min-height:64px;padding:12px}#sanaexpert-chat-widget #sanaexpert-chat-container .chat-header strong{display:none}#sanaexpert-chat-widget #contact-options-container{padding:24px 20px}#sanaexpert-chat-widget .welcome-title{font-size:22px}}\n@media(prefers-reduced-motion:reduce){#sanaexpert-chat-widget *,#sanaexpert-chat-widget *::before,#sanaexpert-chat-widget *::after{animation:none!important;transition:none!important}#sanaexpert-chat-widget .typing-animation .typing-status{position:static;width:auto;height:auto;margin:0;clip:auto;white-space:normal;font-size:13px;line-height:1.4}}\n#sanaexpert-chat-widget[data-inline=\"true\"] #sanaexpert-chat-container{position:relative;right:auto;bottom:auto;width:100%;max-width:760px;height:640px;max-height:80vh;z-index:auto}\n#sanaexpert-chat-widget [hidden]{display:none!important}\n\n/* Antworten bleiben auch bei abweichenden Auswahleinstellungen des Shop-Themes kopierbar. */\n#sanaexpert-chat-widget #sanaexpert-chat-container .chat-answer{user-select:text;-webkit-user-select:text}\n#sanaexpert-chat-widget .actions-container{display:flex;flex-wrap:wrap;gap:8px;margin-top:12px;white-space:normal}\n#sanaexpert-chat-widget .actions-container a,#sanaexpert-chat-widget .actions-container button{display:block;min-height:44px;padding:10px 14px;border:0;border-radius:10px;background:var(--chat-green);color:#fff;text-decoration:none;text-align:center;font-size:14px;font-weight:600;line-height:1.5}\n#sanaexpert-chat-widget .copy-answer{display:block;min-height:36px;margin-top:12px;padding:6px 10px;border:1px solid var(--border-color);border-radius:8px;background:var(--secondary-color);color:var(--chat-link);font-size:12px;line-height:1.5;white-space:normal;text-align:left}\n";
document.body.appendChild(style);
window.SanaChatWidget = { mount: mountWidget, renderMessage, messageText };
const script = document.currentScript;
if (script && script.dataset.endpoint && script.dataset.autoMount !== 'false') mountWidget(document.body, {
  endpoint: script.dataset.endpoint,
  cemail: script.dataset.cemail,
  cname: script.dataset.cname,
  language: script.dataset.language,
  defaultLanguage: script.dataset.defaultLanguage,
  botId: script.dataset.botId,
  environment: script.dataset.environment,
  storageKey: script.dataset.storageKey,
  branding: { name: script.dataset.name, logoUrl: script.dataset.logoUrl }
});
